/**
 * Weekend Income Blueprint — Opt-In Landing Page
 * Stack: React + TypeScript + Tailwind CSS + RTK Query
 *
 * RTK Query setup is self-contained in this file for portability.
 * In a real project, split store / api / slice into separate files.
 */

import React, { useState, useEffect, useRef } from "react";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

// ─── RTK Query API ────────────────────────────────────────────────────────────

interface LeadPayload {
  firstName: string;
  whatsapp: string;
  source: string;
  timestamp: string;
}

interface LeadResponse {
  success: boolean;
  message: string;
}

const leadApi = createApi({
  reducerPath: "leadApi",
  baseQuery: fetchBaseQuery({ baseUrl: "/api" }),
  endpoints: (builder) => ({
    submitLead: builder.mutation<LeadResponse, LeadPayload>({
      query: (body) => ({
        url: "/leads",
        method: "POST",
        body,
      }),
    }),
  }),
});

const { useSubmitLeadMutation } = leadApi;

// ─── Constants ────────────────────────────────────────────────────────────────

const BULLETS: { icon: string; text: string }[] = [
  {
    icon: "₦",
    text: "The exact 3 products that generate ₦500k/month — and what to charge for each",
  },
  {
    icon: "⏱",
    text: "A 60-minute exercise that tells you exactly what YOU should sell",
  },
  {
    icon: "🗓",
    text: "The one-weekend build plan — from zero to three sellable products by Sunday evening",
  },
  {
    icon: "⚡",
    text: "Why most Nigerian digital businesses fail in month 2 — and how to build one that doesn't",
  },
];

// ─── Sub-components ───────────────────────────────────────────────────────────

const GoldDivider: React.FC = () => (
  <div className="flex items-center gap-3 my-6">
    <div className="flex-1 h-px bg-amber-600/30" />
    <div className="w-1.5 h-1.5 rotate-45 bg-amber-500" />
    <div className="flex-1 h-px bg-amber-600/30" />
  </div>
);

const Checkmark: React.FC = () => (
  <svg
    className="w-4 h-4 text-amber-400 shrink-0 mt-0.5"
    viewBox="0 0 16 16"
    fill="none"
  >
    <circle cx="8" cy="8" r="7.5" stroke="currentColor" strokeWidth="1" />
    <path
      d="M5 8l2 2 4-4"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

interface StatBadgeProps {
  value: string;
  label: string;
  animate: boolean;
}
const StatBadge: React.FC<StatBadgeProps> = ({ value, label, animate }) => (
  <div
    className={`text-center transition-all duration-700 ${
      animate ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
    }`}
  >
    <div className="text-2xl font-bold text-amber-400 font-serif">{value}</div>
    <div className="text-xs text-emerald-300/70 mt-0.5 leading-tight">
      {label}
    </div>
  </div>
);

// ─── Form ────────────────────────────────────────────────────────────────────

const OptInForm: React.FC = () => {
  const [firstName, setFirstName] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [localError, setLocalError] = useState("");
  const [focused, setFocused] = useState<"name" | "wa" | null>(null);

  const [submitLead, { isLoading, error }] = useSubmitLeadMutation();

  const validate = () => {
    if (!firstName.trim()) return "Please enter your first name.";
    const cleaned = whatsapp.replace(/\D/g, "");
    if (cleaned.length < 10) return "Please enter a valid WhatsApp number.";
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      setLocalError(err);
      return;
    }
    setLocalError("");

    try {
      await submitLead({
        firstName: firstName.trim(),
        whatsapp: whatsapp.trim(),
        source: "weekend-income-blueprint",
        timestamp: new Date().toISOString(),
      }).unwrap();
      setSubmitted(true);
    } catch {
      // Error displayed via RTK error state
    }
  };

  if (submitted) {
    return (
      <div className="flex flex-col items-center justify-center py-10 px-4 text-center space-y-4 animate-fade-in">
        <div className="w-16 h-16 rounded-full bg-amber-500/20 border border-amber-400/40 flex items-center justify-center">
          <svg
            className="w-8 h-8 text-amber-400"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path
              d="M5 13l4 4L19 7"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <h3 className="text-xl font-bold text-white font-serif">
          You're in, {firstName}!
        </h3>
        <p className="text-emerald-200/80 text-sm leading-relaxed max-w-xs">
          Your free guide is on its way to your WhatsApp right now. Check for a
          message from us within the next 60 seconds.
        </p>
        <div className="mt-2 text-xs text-emerald-300/50">
          While you wait — save this number as{" "}
          <span className="text-amber-400 font-medium">Wealth Method</span> so
          you don't miss the guide.
        </div>
      </div>
    );
  }

  const apiError =
    error && "data" in error
      ? (error.data as { message?: string })?.message
      : error
        ? "Something went wrong. Please try again."
        : null;

  const displayError = localError || apiError;

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-4">
      {/* First Name */}
      <div className="relative">
        <label className="block text-xs font-semibold text-emerald-300/70 uppercase tracking-widest mb-1.5">
          First Name
        </label>
        <div
          className={`relative rounded-lg overflow-hidden transition-all duration-300 ${
            focused === "name"
              ? "ring-2 ring-amber-400/60 shadow-lg shadow-amber-900/20"
              : "ring-1 ring-emerald-700/50"
          }`}
        >
          <div className="absolute inset-y-0 left-0 w-0.5 bg-amber-400 transition-opacity duration-300 opacity-0 group-focus-within:opacity-100" />
          <input
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            onFocus={() => setFocused("name")}
            onBlur={() => setFocused(null)}
            placeholder="e.g. Chiamaka"
            className="w-full bg-emerald-950/60 text-white placeholder-emerald-600/60 px-4 py-3.5 text-sm focus:outline-none font-medium"
          />
        </div>
      </div>

      {/* WhatsApp */}
      <div className="relative">
        <label className="block text-xs font-semibold text-emerald-300/70 uppercase tracking-widest mb-1.5">
          WhatsApp Number
        </label>
        <div
          className={`relative rounded-lg overflow-hidden transition-all duration-300 ${
            focused === "wa"
              ? "ring-2 ring-amber-400/60 shadow-lg shadow-amber-900/20"
              : "ring-1 ring-emerald-700/50"
          }`}
        >
          <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
            <span className="text-emerald-500/60 text-sm font-medium">🇳🇬</span>
          </div>
          <input
            type="tel"
            value={whatsapp}
            onChange={(e) => setWhatsapp(e.target.value)}
            onFocus={() => setFocused("wa")}
            onBlur={() => setFocused(null)}
            placeholder="+234 812 345 6789"
            className="w-full bg-emerald-950/60 text-white placeholder-emerald-600/60 pl-10 pr-4 py-3.5 text-sm focus:outline-none font-medium"
          />
        </div>
      </div>

      {/* Error */}
      {displayError && (
        <p className="text-red-400 text-xs flex items-center gap-1.5">
          <span>⚠</span> {displayError}
        </p>
      )}

      {/* Submit */}
      <button
        type="submit"
        disabled={isLoading}
        className="group relative w-full overflow-hidden rounded-lg font-bold text-sm tracking-wide transition-all duration-300 disabled:opacity-60"
      >
        {/* Background layers */}
        <div className="absolute inset-0 bg-gradient-to-r from-amber-500 to-amber-400 transition-transform duration-300 group-hover:scale-105" />
        <div className="absolute inset-0 bg-gradient-to-t from-amber-600/40 to-transparent" />

        {/* Shimmer */}
        <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-white/20 to-transparent skew-x-12" />

        <span className="relative flex items-center justify-center gap-2 py-4 text-emerald-950">
          {isLoading ? (
            <>
              <svg
                className="animate-spin w-4 h-4"
                viewBox="0 0 24 24"
                fill="none"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                />
              </svg>
              Sending your guide…
            </>
          ) : (
            <>
              Send Me the Free Guide Now
              <svg
                className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1"
                viewBox="0 0 16 16"
                fill="currentColor"
              >
                <path d="M8 0l8 8-8 8-1.4-1.4L12.2 9H0V7h12.2L6.6 1.4z" />
              </svg>
            </>
          )}
        </span>
      </button>

      {/* Trust line */}
      <p className="text-center text-xs text-emerald-500/60 leading-relaxed px-2">
        You'll receive the guide directly on WhatsApp within 60 seconds.{" "}
        <span className="text-emerald-400/70">
          No spam. No daily broadcast unless you choose to stay.
        </span>
      </p>
    </form>
  );
};

// ─── Hero / Left Panel ────────────────────────────────────────────────────────

const HeroPanel: React.FC<{ statsVisible: boolean }> = ({ statsVisible }) => {
  return (
    <div className="flex flex-col justify-center space-y-8">
     

      {/* Headline */}
      <div className="space-y-3">
        <h1 className="text-3xl md:text-4xl lg:text-[2.6rem] font-bold leading-tight text-white font-serif">
          The <span className="text-amber-400 italic">3-Product System</span>{" "}
          for Making
          <br />
          <span className="text-emerald-300">₦500,000/Month</span>
          <br />
          <span className="text-white/90 font-normal">on Weekends</span>
        </h1>
        <p className="text-emerald-200/70 text-base leading-relaxed max-w-lg">
          Built for Nigerian Professionals. Learn how to turn what you already
          know into three digital products that sell automatically — without
          quitting your job, without tech skills, and without starting from
          scratch.
        </p>
      </div>

      <GoldDivider />

      {/* Bullets */}
      <ul className="space-y-4">
        {BULLETS.map((b, i) => (
          <li
            key={i}
            className="flex items-start gap-3 group"
            style={{
              animationDelay: `${i * 100}ms`,
            }}
          >
            <Checkmark />
            <span className="text-sm text-emerald-100/90 leading-snug group-hover:text-white transition-colors duration-200">
              {b.text}
            </span>
          </li>
        ))}
      </ul>

      <GoldDivider />

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { value: "₦500k", label: "Monthly income\ntarget" },
          { value: "48hrs", label: "To build all\nthree products" },
          { value: "90 days", label: "To consistent\nrevenue" },
        ].map((s, i) => (
          <StatBadge
            key={i}
            value={s.value}
            label={s.label}
            animate={statsVisible}
          />
        ))}
      </div>

      {/* Social proof ticker */}
    </div>
  );
};

// ─── Form Panel / Right ───────────────────────────────────────────────────────

const FormPanel: React.FC = () => {
  return (
    <div className="relative">
      {/* Glow */}
      <div className="absolute -inset-px rounded-2xl bg-gradient-to-b from-amber-500/20 to-emerald-700/10 blur-sm" />

      <div className="relative bg-gradient-to-b from-emerald-900/80 to-emerald-950/90 backdrop-blur-sm border border-emerald-700/40 rounded-2xl overflow-hidden shadow-2xl shadow-emerald-950/60">
        {/* Top accent bar */}
        <div className="h-1 bg-gradient-to-r from-amber-500 via-amber-400 to-amber-500" />

        <div className="px-6 pt-6 pb-8 space-y-6">
          {/* Panel heading */}
          <div className="text-center space-y-1">
            <p className="text-xs font-bold text-amber-400 uppercase tracking-[0.2em]">
              Get Instant Access
            </p>
            <h2 className="text-xl font-bold text-white font-serif leading-snug">
              Your Free Guide Is One Step Away
            </h2>
            <p className="text-xs text-emerald-400/60 leading-relaxed">
              Enter your details below and we'll send it directly to your
              WhatsApp.
            </p>
          </div>

          

          {/* Form */}
          <OptInForm />

          {/* Security badges */}
          <div className="flex items-center justify-center gap-4 pt-2">
            {["🔒 Private", "✓ No spam", "↩ Unsubscribe anytime"].map(
              (badge, i) => (
                <span
                  key={i}
                  className="text-[10px] text-emerald-600/60 font-medium"
                >
                  {badge}
                </span>
              ),
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Footer ───────────────────────────────────────────────────────────────────

const PageFooter: React.FC = () => (
  <footer className="border-t border-emerald-800/40 py-8">
    <div className="max-w-5xl mx-auto px-6">
     

      <div className="mt-6 pt-4 border-t border-emerald-900/60 flex flex-col md:flex-row items-center justify-between gap-2">
        <p className="text-[10px] text-emerald-700/50">
          © 2025 The Wealth Method · All rights reserved
        </p>
        <p className="text-[10px] text-emerald-700/50">
          Privacy Policy · No credit card required · Delivered via WhatsApp
        </p>
      </div>
    </div>
  </footer>
);

// ─── Main Page ────────────────────────────────────────────────────────────────

export const OptInPage: React.FC = () => {
  const [statsVisible, setStatsVisible] = useState(false);
  const statsRef = useRef<HTMLDivElement>(null);

  // Trigger stats animation on scroll into view
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setStatsVisible(true);
      },
      { threshold: 0.3 },
    );
    if (statsRef.current) observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      className="min-h-screen bg-emerald-950 text-white"
      style={{ fontFamily: "'Georgia', serif" }}
    >
      {/* Atmospheric background */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        {/* Noise texture overlay */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
            backgroundSize: "200px",
          }}
        />
        {/* Radial gradient blobs */}
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] rounded-full bg-emerald-800/10 blur-3xl -translate-y-1/2" />
        <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] rounded-full bg-amber-900/8 blur-3xl translate-y-1/2" />
        {/* Grid lines */}
        <div
          className="absolute inset-0 opacity-[0.015]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(52,211,153,1) 1px, transparent 1px), linear-gradient(90deg, rgba(52,211,153,1) 1px, transparent 1px)",
            backgroundSize: "64px 64px",
          }}
        />
      </div>

      {/* Top announcement bar */}
      <div className="relative bg-gradient-to-r from-amber-600 via-amber-500 to-amber-600 py-2.5 px-4 text-center">
        <p className="text-xs font-bold text-emerald-950 uppercase tracking-wider">
          🎁 Free Guide — The Weekend Income Blueprint · Available for a limited
          time ·
          <span className="ml-2 underline underline-offset-2 cursor-pointer hover:no-underline">
            Get it now ↓
          </span>
        </p>
      </div>

      {/* Header */}
     

      {/* Hero section */}
      <main className="relative">
        <div className="max-w-5xl mx-auto px-6 py-12 md:py-16">
          <div className="grid lg:grid-cols-[1fr_420px] gap-12 lg:gap-16 items-start">
            {/* Left — hero copy */}
            <div ref={statsRef}>
              <HeroPanel statsVisible={statsVisible} />
            </div>

            {/* Right — form */}
            <div className="lg:sticky lg:top-8">
              <FormPanel />
            </div>
          </div>
        </div>

        {/* Full-width trust band */}
        <div className="border-y border-emerald-800/40 bg-emerald-900/20 py-6">
          <div className="max-w-5xl mx-auto px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                {
                  icon: "📱",
                  label: "Delivered to WhatsApp",
                  sub: "Within 60 seconds",
                },
                {
                  icon: "🇳🇬",
                  label: "Built for Nigeria",
                  sub: "Naira amounts & tools",
                },
                {
                  icon: "🔒",
                  label: "Your data is safe",
                  sub: "Never shared or sold",
                },
                { icon: "✦", label: "100% free", sub: "No card required" },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3">
                  <span className="text-xl">{item.icon}</span>
                  <div>
                    <p className="text-xs font-bold text-white">{item.label}</p>
                    <p className="text-xs text-emerald-500/60">{item.sub}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <PageFooter />
    </div>
  );
};



/**
 * ─── USAGE NOTES ────────────────────────────────────────────────────────────
 *
 * 1. DEPENDENCIES
 *    npm install @reduxjs/toolkit react-redux
 *    npm install -D tailwindcss @types/react
 *
 * 2. TAILWIND CONFIG (tailwind.config.ts)
 *    content: ["./src/**\/*.{ts,tsx}"]
 *    The file uses only core Tailwind utilities — no custom config required.
 *
 * 3. BACKEND ENDPOINT
 *    The form POSTs to POST /api/leads with:
 *    { firstName, whatsapp, source, timestamp }
 *
 *    Replace baseUrl in leadApi with your actual API URL.
 *    For testing without a backend, mock the mutation:
 *    change submitLead() to a setTimeout-based local state update.
 *
 * 4. PLACEHOLDERS TO FILL IN
 *    • [Your Name]          — author name (2 locations in footer)
 *    • Book cover image     — replace the CSS book mock in PageFooter
 *    • WhatsApp delivery    — connect /api/leads to Wati / Twilio / ManyChat
 *    • Pricing ₦7,500       — appears in the guide, not this page directly
 *
 * 5. FONTS (optional upgrade)
 *    Add to index.html:
 *    <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,600;0,700;1,400&family=DM+Sans:wght@400;500;600&display=swap" rel="stylesheet">
 *    Then update font-serif to font-['Cormorant_Garamond'] in Tailwind.
 */
